import { useState, useEffect } from "react";
import axios from "../../AxiosInstance";
import Sidebar from "../../components/Sidebar";
import toast from "react-hot-toast";
import { FaEdit, FaTrash, FaPlus, FaGlobe, FaStore, FaLink } from "react-icons/fa";

const StoreManagement = () => {
  const [stores, setStores] = useState([]);
  const [storeFields, setStoreFields] = useState([{ name: "", link: "" }]);
  const [country, setCountry] = useState("");
  const [editingId, setEditingId] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [addingToCountry, setAddingToCountry] = useState(null); // Track which country we're adding to
  const [newStoreForCountry, setNewStoreForCountry] = useState({ name: "", link: "" });

  // Fetch stores
  useEffect(() => {
    fetchStores();
  }, []);

  const fetchStores = async () => {
    setIsLoading(true);
    try {
      const res = await axios.get("api/v1/stores", {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
      });
      setStores(res.data || []);
    } catch (error) {
      console.error("Error fetching stores:", error);
      toast.error("Failed to fetch stores");
      setStores([]);
    } finally {
      setIsLoading(false);
    }
  };

  // Handle input changes
  const handleCountryChange = (e) => setCountry(e.target.value);

  const handleStoreChange = (index, e) => {
    const newFields = [...storeFields];
    newFields[index][e.target.name] = e.target.value;
    setStoreFields(newFields);
  };

  // Add more store input fields
  const handleAddMore = () => {
    setStoreFields([...storeFields, { name: "", link: "" }]);
  };

  const handleRemoveStoreField = (index) => {
    if (storeFields.length > 1) {
      setStoreFields(storeFields.filter((_, i) => i !== index));
    }
  };

  // Handle adding store to existing country
  const handleAddStoreToCountry = async (countryName) => {
    if (!newStoreForCountry.name || !newStoreForCountry.link) {
      toast.error("Please fill in store name and link");
      return;
    }

    setIsLoading(true);
    try {
      await axios.post(
        "api/v1/stores",
        [{
          country: countryName,
          name: newStoreForCountry.name,
          link: newStoreForCountry.link,
        }],
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );
      toast.success("Store added successfully");
      setNewStoreForCountry({ name: "", link: "" });
      setAddingToCountry(null);
      fetchStores();
    } catch (error) {
      console.error("Error adding store:", error);
      toast.error(error.response?.data?.message || "Failed to add store");
    } finally {
      setIsLoading(false);
    }
  };

  // Handle form submission (Add & Update)
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (country && storeFields.length > 0 && storeFields.some(s => s.name && s.link)) {
      setIsLoading(true);
      try {
        if (editingId) {
          // Update existing store
          await axios.put(
            `api/v1/stores/${editingId}`,
            {
              country,
              ...storeFields[0],
            },
            {
              headers: {
                Authorization: `Bearer ${localStorage.getItem("token")}`,
              },
            }
          );
          toast.success("Store updated successfully");
        } else {
          // Add new stores (for new countries)
          const storesToAdd = storeFields
            .filter((store) => store.name && store.link)
            .map((store) => ({
              country,
              ...store,
            }));
          
          if (storesToAdd.length > 0) {
            await axios.post(
              "api/v1/stores",
              storesToAdd,
              {
                headers: {
                  Authorization: `Bearer ${localStorage.getItem("token")}`,
                },
              }
            );
            toast.success("Stores added successfully");
          }
        }

        // Reset form
        setStoreFields([{ name: "", link: "" }]);
        setCountry("");
        setEditingId(null);
        fetchStores();
      } catch (error) {
        console.error("Error saving stores:", error);
        toast.error(error.response?.data?.message || "Failed to save stores");
      } finally {
        setIsLoading(false);
      }
    } else {
      toast.error("Please fill in all required fields");
    }
  };

  // Handle delete
  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this store?")) {
      return;
    }

    try {
      await axios.delete(`api/v1/stores/${id}`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
      });
      toast.success("Store deleted successfully");
      setStores((prevStores) =>
        prevStores
          .map((group) => ({
            ...group,
            stores: group.stores.filter((store) => store._id !== id),
          }))
          .filter((group) => group.stores.length > 0)
      );
    } catch (error) {
      console.error("Delete error:", error);
      toast.error("Failed to delete store");
    }
  };

  // Handle Edit
  const handleEdit = (store, countryName) => {
    setEditingId(store._id);
    setCountry(countryName);
    setStoreFields([{ name: store.name, link: store.link }]);
    // Scroll to form
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleCancelEdit = () => {
    setEditingId(null);
    setCountry("");
    setStoreFields([{ name: "", link: "" }]);
  };

  return (
    <div className="flex h-screen overflow-hidden">
      <Sidebar />
      <div className="flex-1 flex flex-col p-4 overflow-hidden">
        <h1 className="text-2xl font-bold text-gray-800 mb-6 flex-shrink-0">
          Buy On Menu Management
        </h1>

        {/* Add/Edit Store Form */}
        <div className="bg-white rounded-lg shadow-md p-6 mb-6 flex-shrink-0">
          <h2 className="text-xl font-semibold text-gray-700 mb-4">
            {editingId ? "Edit Store" : "Add New Country with Stores"}
          </h2>
          {!editingId && (
            <p className="text-sm text-gray-500 mb-4">
              Use this form to add a new country. To add stores to an existing country, use the "Add Store" button in the country card below.
            </p>
          )}
          
          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Country Input */}
            <div className="flex flex-col sm:flex-row sm:items-center gap-3">
              <label className="text-sm font-medium text-gray-700 sm:w-32 flex items-center gap-2">
                <FaGlobe className="text-[#34c3c9]" />
                Country:
              </label>
              <input
                type="text"
                placeholder="Enter Country Name"
                value={country}
                onChange={handleCountryChange}
                required
                className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#34c3c9] focus:border-transparent"
              />
            </div>

            {/* Store Fields */}
            {storeFields.map((store, index) => (
              <div key={index} className="border border-gray-200 rounded-lg p-4 bg-gray-50">
                <div className="flex items-center justify-between mb-3">
                  <span className="text-sm font-medium text-gray-600">
                    Store {index + 1}
                  </span>
                  {storeFields.length > 1 && !editingId && (
                    <button
                      type="button"
                      onClick={() => handleRemoveStoreField(index)}
                      className="text-red-600 hover:text-red-700 text-sm font-medium"
                    >
                      Remove
                    </button>
                  )}
                </div>
                
                <div className="space-y-3">
                  <div className="flex flex-col sm:flex-row sm:items-center gap-3">
                    <label className="text-sm font-medium text-gray-700 sm:w-32 flex items-center gap-2">
                      <FaStore className="text-[#34c3c9]" />
                      Store Name:
                    </label>
                    <input
                      type="text"
                      name="name"
                      placeholder="Enter Store Name"
                      value={store.name}
                      onChange={(e) => handleStoreChange(index, e)}
                      required
                      className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#34c3c9] focus:border-transparent"
                    />
                  </div>
                  
                  <div className="flex flex-col sm:flex-row sm:items-center gap-3">
                    <label className="text-sm font-medium text-gray-700 sm:w-32 flex items-center gap-2">
                      <FaLink className="text-[#34c3c9]" />
                      Store Link:
                    </label>
                    <input
                      type="url"
                      name="link"
                      placeholder="https://example.com"
                      value={store.link}
                      onChange={(e) => handleStoreChange(index, e)}
                      required
                      className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#34c3c9] focus:border-transparent"
                    />
                  </div>
                </div>
              </div>
            ))}

            {/* Action Buttons */}
            <div className="flex flex-wrap gap-3 pt-2">
              {!editingId && (
                <button
                  type="button"
                  onClick={handleAddMore}
                  className="px-4 py-2 text-sm font-medium text-[#34c3c9] border border-[#34c3c9] rounded-lg hover:bg-[#34c3c9] hover:text-white transition-colors flex items-center gap-2"
                >
                  <FaPlus />
                  Add More Store
                </button>
              )}
              
              {editingId && (
                <button
                  type="button"
                  onClick={handleCancelEdit}
                  className="px-4 py-2 text-sm font-medium text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-100 transition-colors"
                >
                  Cancel
                </button>
              )}
              
              <button
                type="submit"
                disabled={isLoading}
                className="px-6 py-2 text-sm font-medium text-white bg-[#34c3c9] rounded-lg hover:bg-[#2ba8ae] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isLoading
                  ? "Saving..."
                  : editingId
                  ? "Update Store"
                  : "Add Store"}
              </button>
            </div>
          </form>
        </div>

        {/* Stores List */}
        <div className="flex-1 overflow-auto min-h-0">
          {isLoading && stores.length === 0 ? (
            <div className="flex items-center justify-center h-full">
              <div className="text-center">
                <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-[#34c3c9]"></div>
                <p className="mt-2 text-gray-600">Loading stores...</p>
              </div>
            </div>
          ) : stores && stores.length > 0 ? (
            <div className="space-y-6">
              <h2 className="text-xl font-semibold text-gray-800">Store List</h2>
              {stores.map((group) => (
                <div
                  key={group.country}
                  className="bg-white rounded-lg shadow-md p-6"
                >
                  <div className="flex items-center gap-2 mb-4 pb-3 border-b border-gray-200">
                    <FaGlobe className="text-[#34c3c9] text-xl" />
                    <h3 className="text-lg font-bold text-gray-800">
                      {group.country}
                    </h3>
                    <span className="ml-auto text-sm text-gray-500 bg-gray-100 px-2 py-1 rounded">
                      {group.stores?.length || 0} store(s)
                    </span>
                    <button
                      onClick={() => setAddingToCountry(addingToCountry === group.country ? null : group.country)}
                      className="ml-2 px-3 py-1.5 text-sm font-medium text-white bg-[#34c3c9] rounded-lg hover:bg-[#2ba8ae] transition-colors flex items-center gap-2"
                    >
                      <FaPlus />
                      Add Store
                    </button>
                  </div>

                  {/* Add Store Form for Existing Country */}
                  {addingToCountry === group.country && (
                    <div className="mb-4 p-4 bg-gray-50 rounded-lg border border-gray-200">
                      <h4 className="text-sm font-semibold text-gray-700 mb-3">
                        Add Store to {group.country}
                      </h4>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Store Name
                          </label>
                          <input
                            type="text"
                            placeholder="Enter Store Name"
                            value={newStoreForCountry.name}
                            onChange={(e) => setNewStoreForCountry({ ...newStoreForCountry, name: e.target.value })}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#34c3c9] focus:border-transparent"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Store Link
                          </label>
                          <input
                            type="url"
                            placeholder="https://example.com"
                            value={newStoreForCountry.link}
                            onChange={(e) => setNewStoreForCountry({ ...newStoreForCountry, link: e.target.value })}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#34c3c9] focus:border-transparent"
                          />
                        </div>
                      </div>
                      <div className="flex gap-2 mt-3">
                        <button
                          onClick={() => handleAddStoreToCountry(group.country)}
                          disabled={isLoading}
                          className="px-4 py-2 text-sm font-medium text-white bg-[#34c3c9] rounded-lg hover:bg-[#2ba8ae] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          {isLoading ? "Adding..." : "Add Store"}
                        </button>
                        <button
                          onClick={() => {
                            setAddingToCountry(null);
                            setNewStoreForCountry({ name: "", link: "" });
                          }}
                          className="px-4 py-2 text-sm font-medium text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-100 transition-colors"
                        >
                          Cancel
                        </button>
                      </div>
                    </div>
                  )}
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {group.stores &&
                      group.stores.map((store) => (
                        <div
                          key={store._id}
                          className="border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow bg-gray-50"
                        >
                          <div className="flex items-start justify-between mb-3">
                            <div className="flex-1">
                              <h4 className="font-semibold text-gray-800 mb-1 flex items-center gap-2">
                                <FaStore className="text-[#34c3c9]" />
                                {store.name}
                              </h4>
                              <a
                                href={store.link}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-sm text-[#34c3c9] hover:underline flex items-center gap-1 truncate"
                              >
                                <FaLink className="text-xs" />
                                {store.link}
                              </a>
                            </div>
                          </div>
                          
                          <div className="flex gap-2 mt-3">
                            <button
                              onClick={() => handleEdit(store, group.country)}
                              className="flex-1 px-3 py-2 text-sm font-medium text-blue-600 bg-blue-50 rounded-lg hover:bg-blue-100 transition-colors flex items-center justify-center gap-2"
                            >
                              <FaEdit />
                              Edit
                            </button>
                            <button
                              onClick={() => handleDelete(store._id)}
                              className="flex-1 px-3 py-2 text-sm font-medium text-red-600 bg-red-50 rounded-lg hover:bg-red-100 transition-colors flex items-center justify-center gap-2"
                            >
                              <FaTrash />
                              Delete
                            </button>
                          </div>
                        </div>
                      ))}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="flex items-center justify-center h-full">
              <div className="text-center">
                <FaStore className="mx-auto text-4xl text-gray-300 mb-3" />
                <p className="text-gray-500 text-lg">No stores available</p>
                <p className="text-gray-400 text-sm mt-1">
                  Add your first store using the form above
                </p>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default StoreManagement;
